import type {
	OnboardingStep,
	OnboardingStepType,
} from "@memorang/constants/config/schema";
import type {
	Category,
	ExamDetails,
	PracticeTestExam,
	StateDetails,
} from "@memorang/types";
import {
	createContext,
	useCallback,
	useContext,
	useMemo,
	useState,
} from "react";
import { useDevice } from "../../useDevice";
import {
	getFilteredExams,
	getStatesForCategory,
	sanitizeProfile,
} from "./helpers";
import type {
	OnboardingCompletionDestination,
	OnboardingProfile,
	OnboardingSelections,
} from "./types";

type State = {
	enabled: boolean;
	steps: OnboardingStep[];
	step: OnboardingStep;
	stepIndex: number;
	displayStepIndex: number;
	displayStepsTotal: number;
	isFirstStep: boolean;
	isLastStep: boolean;
	selectedLearnerTypeId: string | undefined;
	selectedRankId: string | undefined;
	selectedPlanId: string | undefined;
	selectedCategoryId: string | undefined;
	selectedLevelId: string | undefined;
	selectedStateCode: string | undefined;
	selectedExamId: string | undefined;
	selectedExamDate: Date | undefined;
	selectedSkillIds: string[];
	selectedProfile: OnboardingProfile | undefined;
	canGoNext: boolean;
	totalItemCount: number | undefined;
	exams: ExamDetails[] | undefined;
	categories: Category[] | undefined;
	diagnostics: PracticeTestExam[];
	derivedStates: StateDetails[];
	filteredExams: ExamDetails[];
	selectedDiagnostic: PracticeTestExam | undefined;
	isCompletionPending: boolean;
	errors:
		| {
				profile?: string;
		  }
		| undefined;
};

type Actions = {
	onGoToNextStep: () => void;
	onGoToPreviousStep: () => void;
	onSelectExam: (id: string) => void;
	onSelectCategory: (id: string) => void;
	onSelectLevel: (id: string) => void;
	onSelectState: (code: string) => void;
	onSelectLearnerType: (id: string) => void;
	onSelectRank: (id: string) => void;
	onSelectPlan: (id: string) => void;
	onToggleSkill: (id: string) => void;
	onUpdateProfile: (profile: OnboardingProfile) => void;
	onExamDateChange: (date: Date) => void;
	onSelectDiagnostic: (diagnostic: PracticeTestExam | undefined) => void;
	onComplete:
		| ((
				selections: OnboardingSelections,
				destination: OnboardingCompletionDestination,
		  ) => void)
		| undefined;
};

type Props = {
	steps: OnboardingStep[];
	children: React.ReactNode;
	step?: OnboardingStepType; // Controlled step
	exams?: ExamDetails[];
	categories?: Category[];
	diagnostics?: PracticeTestExam[];
	isCompletionPending?: boolean;
	totalItemCount?: number;
	initialSelections?: {
		categoryId?: string;
		levelId?: string;
		stateCode?: string;
		examId?: string;
		examDate?: Date;
		learnerTypeId?: string;
		rankId?: string;
		planId?: string;
		skillIds?: string[];
		profile?: OnboardingProfile;
	};
	errors?: {
		profile?: string;
	};
	onCategoryChange?: (id: string) => void;
	onLevelChange?: (id: string) => void;
	onStateChange?: (code: string) => void;
	onExamChange?: (id: string) => void;
	onLearnerTypeChange?: (id: string) => void;
	onRankChange?: (id: string) => void;
	onPlanChange?: (id: string) => void;
	onSkillChange?: (ids: string[]) => void;
	onProfileChange?: (profile: OnboardingProfile) => void;
	onExamDateChange?: (date: Date) => void;
	onStepChange?: (args: {
		direction: "next" | "previous";
		from: OnboardingStep;
		to: OnboardingStep;
	}) => void;
	onComplete?: (
		selections: OnboardingSelections,
		destination: OnboardingCompletionDestination,
	) => void;
};

const OnboardingContext = createContext<(State & Actions) | null>(null);

const OnboardingProvider = ({
	children,
	onExamChange,
	onCategoryChange,
	onLevelChange,
	onStateChange,
	onLearnerTypeChange,
	onRankChange,
	onPlanChange,
	onSkillChange,
	onProfileChange,
	onExamDateChange,
	onStepChange,
	onComplete,
	exams,
	categories,
	diagnostics = [],
	isCompletionPending = false,
	initialSelections,
	totalItemCount,
	step: controlledStepType,
	errors,
	...props
}: Props) => {
	const { isMobile } = useDevice();

	const steps = useMemo(() => {
		const enabled = props.steps.filter((s) => s.enabled);
		return isMobile
			? enabled.filter((s) => (s.isMobile != null ? s.isMobile : true))
			: enabled;
	}, [props.steps, isMobile]);

	const [uncontrolledState, setStepState] = useState<{
		step: OnboardingStep;
		stepIndex: number;
	}>({
		step: steps[0],
		stepIndex: 0,
	});

	const { step, stepIndex } = useMemo(() => {
		if (controlledStepType) {
			const idx = steps.findIndex((s) => s.step === controlledStepType);
			const resolvedIdx = idx >= 0 ? idx : 0;
			return { step: steps[resolvedIdx], stepIndex: resolvedIdx };
		}
		return uncontrolledState;
	}, [controlledStepType, steps, uncontrolledState]);

	const [selections, setSelections] = useState<{
		examId: string | undefined;
		categoryId: string | undefined;
		levelId: string | undefined;
		stateCode: string | undefined;
		learnerTypeId: string | undefined;
		rankId: string | undefined;
		planId: string | undefined;
		skillIds: string[];
		profile: OnboardingProfile | undefined;
		examDate: Date | undefined;
	}>({
		examId: initialSelections?.examId,
		categoryId: initialSelections?.categoryId,
		levelId: initialSelections?.levelId,
		stateCode: initialSelections?.stateCode,
		learnerTypeId: initialSelections?.learnerTypeId,
		rankId: initialSelections?.rankId,
		planId: initialSelections?.planId,
		skillIds: initialSelections?.skillIds ?? [],
		profile: initialSelections?.profile,
		examDate: initialSelections?.examDate,
	});

	const {
		examId: selectedExamId,
		categoryId: selectedCategoryId,
		levelId: selectedLevelId,
		stateCode: selectedStateCode,
		learnerTypeId: selectedLearnerTypeId,
		rankId: selectedRankId,
		planId: selectedPlanId,
		skillIds: selectedSkillIds,
		profile: selectedProfile,
	} = selections;

	const selectedExamDate = selections.examDate ?? initialSelections?.examDate;
	const [selectedDiagnostic, setSelectedDiagnostic] = useState<
		PracticeTestExam | undefined
	>();

	const categoryHasState =
		categories?.find((c) => c.id === selectedCategoryId)?.hasState ?? false;

	const shouldSkip = (s: OnboardingStep): boolean =>
		(s.step === "chooseLevel" && !categoryHasState) ||
		(s.step === "chooseState" &&
			(!categoryHasState || selectedLevelId === "national"));

	const findNextIndex = (from: number, dir: 1 | -1): number => {
		let i = from + dir;
		while (i >= 0 && i < steps.length && shouldSkip(steps[i])) {
			i += dir;
		}
		return i;
	};

	const visibleSteps = steps.filter((s) => !shouldSkip(s));
	const displayedSteps = visibleSteps.filter(
		(s) => s.step !== "welcome" && s.step !== "review",
	);
	const displayStepsTotal = displayedSteps.length;
	const displayStepIndex =
		displayedSteps.findIndex((s) => s.step === step?.step) + 1;

	const isFirstStep = stepIndex === 0;
	const isLastStep = findNextIndex(stepIndex, 1) >= steps.length;

	const canGoNext = useMemo(() => {
		switch (step?.step) {
			case "chooseExam":
			case "chooseContext":
				return Boolean(selectedExamId);
			case "chooseCategory":
				return Boolean(selectedCategoryId);
			case "chooseLevel":
				return Boolean(selectedLevelId);
			case "chooseState":
				return Boolean(selectedStateCode);
			case "learnerType":
				return Boolean(selectedLearnerTypeId);
			case "rank":
				return Boolean(selectedRankId);
			case "plan":
				return Boolean(selectedPlanId);
			case "skill":
				return selectedSkillIds.length > 0;
			case "profile":
				return Boolean(selectedProfile?.username) && !errors?.profile;
			case "review":
				return !isCompletionPending;
			default:
				return true;
		}
	}, [
		step?.step,
		selectedExamId,
		selectedCategoryId,
		selectedLevelId,
		selectedStateCode,
		selectedLearnerTypeId,
		selectedRankId,
		selectedPlanId,
		selectedSkillIds,
		selectedProfile,
		errors?.profile,
		isCompletionPending,
	]);

	const derivedStates = useMemo<StateDetails[]>(() => {
		if (!(selectedCategoryId && exams)) {
			return [];
		}
		return getStatesForCategory(exams, selectedCategoryId);
	}, [exams, selectedCategoryId]);

	const filteredExams = useMemo<ExamDetails[]>(() => {
		if (!exams) {
			return [];
		}
		return getFilteredExams(
			exams,
			selectedCategoryId,
			selectedLevelId as "state" | "national" | "state-national" | undefined,
			selectedStateCode,
		);
	}, [exams, selectedCategoryId, selectedLevelId, selectedStateCode]);

	const onGoToNextStep = () => {
		const nextIndex = findNextIndex(stepIndex, 1);
		if (nextIndex >= steps.length) {
			onStepChange?.({ direction: "next", from: step, to: step });
			const completedSelections = {
				profile: selectedProfile,
				learnerTypeId: selectedLearnerTypeId,
				rankId: selectedRankId,
				planId: selectedPlanId,
				skillIds: selectedSkillIds,
				examId: selectedExamId,
				categoryId: selectedCategoryId,
				levelId: selectedLevelId,
				stateCode: selectedStateCode,
			};
			onComplete?.(
				completedSelections,
				selectedDiagnostic
					? { type: "diagnostic", diagnostic: selectedDiagnostic }
					: { type: "home" },
			);
			return;
		}
		const nextStep = steps[nextIndex];
		onStepChange?.({ direction: "next", from: step, to: nextStep });
		if (!controlledStepType) {
			setStepState({ step: nextStep, stepIndex: nextIndex });
		}
	};

	const onGoToPreviousStep = () => {
		if (isFirstStep) {
			return;
		}
		const prevIndex = findNextIndex(stepIndex, -1);
		if (prevIndex < 0) {
			return;
		}
		const prevStep = steps[prevIndex];
		onStepChange?.({ direction: "previous", from: step, to: prevStep });
		if (!controlledStepType) {
			setStepState({ step: prevStep, stepIndex: prevIndex });
		}
	};

	const onSelectExam = (id: string) => {
		setSelections((prev) => ({ ...prev, examId: id }));
		onExamChange?.(id);
	};

	const onSelectCategory = (id: string) => {
		setSelections((prev) => ({
			...prev,
			categoryId: id,
			levelId: undefined,
			stateCode: undefined,
			examId: undefined,
		}));
		onCategoryChange?.(id);
	};

	const onSelectLevel = (id: string) => {
		setSelections((prev) => ({
			...prev,
			levelId: id,
			stateCode: undefined,
			examId: undefined,
		}));
		onLevelChange?.(id);
	};

	const onSelectState = (code: string) => {
		setSelections((prev) => ({ ...prev, stateCode: code, examId: undefined }));
		onStateChange?.(code);
	};

	const onSelectLearnerType = (id: string) => {
		setSelections((prev) => ({ ...prev, learnerTypeId: id }));
		onLearnerTypeChange?.(id);
	};

	const onSelectRank = (id: string) => {
		setSelections((prev) => ({ ...prev, rankId: id }));
		onRankChange?.(id);
	};

	const onSelectPlan = (id: string) => {
		setSelections((prev) => ({ ...prev, planId: id }));
		onPlanChange?.(id);
	};

	const onToggleSkill = (id: string) => {
		setSelections((prev) => {
			const next = prev.skillIds.includes(id)
				? prev.skillIds.filter((s) => s !== id)
				: [...prev.skillIds, id];
			onSkillChange?.(next);
			return { ...prev, skillIds: next };
		});
	};

	const onUpdateProfile = useCallback(
		(nextProfile: OnboardingProfile) => {
			const sanitizedProfile = sanitizeProfile(nextProfile);
			setSelections((prev) => ({ ...prev, profile: sanitizedProfile }));
			onProfileChange?.(sanitizedProfile);
		},
		[onProfileChange],
	);

	const handleExamDateChange = useCallback(
		(date: Date) => {
			setSelections((prev) => ({ ...prev, examDate: date }));
			onExamDateChange?.(date);
		},
		[onExamDateChange],
	);

	return (
		<OnboardingContext.Provider
			value={{
				enabled: steps.length > 0,
				steps,
				step,
				stepIndex,
				displayStepIndex,
				isFirstStep,
				isLastStep,
				displayStepsTotal,
				selectedLearnerTypeId,
				selectedRankId,
				selectedPlanId,
				selectedCategoryId,
				selectedLevelId,
				selectedStateCode,
				selectedExamId,
				selectedExamDate,
				selectedSkillIds,
				selectedProfile,
				canGoNext,
				totalItemCount,
				exams,
				categories,
				diagnostics,
				derivedStates,
				filteredExams,
				selectedDiagnostic,
				isCompletionPending,
				errors,
				onGoToNextStep,
				onGoToPreviousStep,
				onSelectExam,
				onSelectCategory,
				onSelectLevel,
				onSelectState,
				onSelectLearnerType,
				onSelectRank,
				onSelectPlan,
				onToggleSkill,
				onUpdateProfile,
				onExamDateChange: handleExamDateChange,
				onSelectDiagnostic: setSelectedDiagnostic,
				onComplete,
			}}
		>
			{children}
		</OnboardingContext.Provider>
	);
};

const useOnboarding = (): State & Actions => {
	const context = useContext(OnboardingContext);
	if (context === null) {
		throw new Error("useOnboarding must be used within a OnboardingProvider");
	}

	return context;
};

export { OnboardingProvider, useOnboarding };
